// Shared building blocks.

import { ReactNode, createContext, useContext, useEffect, useState } from "react";

// ---- routing (hash based: #/einstellungen/ausgaenge)

export function useRoute(): string[] {
  const read = () => (window.location.hash.replace(/^#\/?/, "") || "dashboard").split("/").filter(Boolean);
  const [parts, setParts] = useState(read);
  useEffect(() => {
    const on = () => setParts(read());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return parts;
}

export function go(path: string) {
  window.location.hash = `#/${path.replace(/^\//, "")}`;
}

// ---- shared state: engine status (polled by the shell) and the signed-in user

export const StatusContext = createContext<any>(null);
export const useStatus = () => useContext(StatusContext);
export const MeContext = createContext<any>(null);
export const useMe = () => useContext(MeContext);
/** The instance all pages work for: a station or the Verbund (chosen in the header). */
export type Instance = { id: string; name: string; color: string; kind: "station" | "verbund" };
export const InstanceContext = createContext<Instance | null>(null);
export const useInstance = () => useContext(InstanceContext);

// ---- help switch (tips per screen)

export const HelpContext = createContext<{ help: boolean; setHelp: (v: boolean) => void }>({ help: false, setHelp: () => {} });

export function Tip({ children }: { children: ReactNode }) {
  const { help } = useContext(HelpContext);
  return help ? <div className="tip">💡 {children}</div> : null;
}

/** "How does this work · In the manual" bar on every page. */
export function HowTo({ text, chapter }: { text: string; chapter: string }) {
  return (
    <div className="hint">
      <span>ⓘ</span>
      <span>{text}</span>
      <span className="spacer" />
      <a href={`#/handbuch/${chapter}`}>Wie funktioniert das · Im Handbuch</a>
    </div>
  );
}

export function PageHead({ title, sub, children, global }: { title: string; sub?: ReactNode; children?: ReactNode; global?: boolean }) {
  const inst = useContext(InstanceContext);
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        <div className="muted">
          {global ? <span className="inst-chip">gilt für alle Sender</span> : inst && <span className="inst-chip" style={{ borderColor: inst.color }}><i style={{ background: inst.color }} />{inst.name}</span>}
          {sub}
        </div>
      </div>
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode }) {
  return (
    <label className="switch">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" />
      {label && <span>{label}</span>}
    </label>
  );
}

export function Field({ label, hint, children, full }: { label: string; hint?: ReactNode; children: ReactNode; full?: boolean }) {
  return (
    <label className={`field${full ? " full" : ""}`}>
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="modal-bg" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={wide ? { width: "min(1180px, 97vw)" } : undefined}>
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <span className="spacer" />
          <button className="iconbtn" onClick={onClose} aria-label="Schließen">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const STATE: Record<string, [string, string]> = {
  verbunden: ["ok", "verbunden"],
  "läuft": ["ok", "läuft"],
  verbinde: ["warn", "verbindet …"],
  fehler: ["bad", "Fehler"],
  aus: ["", "aus"],
  "nicht erreichbar": ["bad", "nicht erreichbar"],
  startet: ["warn", "startet"],
};

export function StateBadge({ state }: { state?: string | null }) {
  const [cls, label] = STATE[state ?? ""] ?? ["", state ?? "–"];
  return (
    <span className={`badge ${cls}`}>
      <span className={`led ${cls}`} />
      {label}
    </span>
  );
}

/** dBFS level bar (−60 … 0) with an optional threshold mark. */
export function Meter({ db, mark }: { db: number | null | undefined; mark?: number }) {
  const pct = (v: number) => Math.max(0, Math.min(100, ((v + 60) / 60) * 100));
  return (
    <div className="meter" title={db == null ? "" : `${db.toFixed(1)} dBFS`}>
      <div style={{ width: `${db == null ? 0 : pct(db)}%` }} />
      {mark !== undefined && <span className="mark" style={{ left: `${pct(mark)}%` }} />}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function ErrorBanner({ error }: { error: string | null | undefined }) {
  return error ? <div className="banner bad">⚠ {error}</div> : null;
}

/** Placeholder for parts of later stages, with what they will do. */
export function Later({ stage, title, points }: { stage: number; title: string; points: string[] }) {
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <span className="badge info">Stufe {stage}</span>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>Dieser Bereich wird in Stufe {stage} gebaut. Geplant:</p>
      <ul>{points.map((p) => <li key={p}>{p}</li>)}</ul>
    </div>
  );
}
